import { useState } from 'react';
import { useClient } from './useClient';
import { useAuthContext } from '../context/AuthContext';
import useSWR from 'swr';

export interface SystemNotification {
  id: string;
  message: string;
  from: string | null;
  until: string | null;
  organizationId: string | null;
  createdAt: string;
}

interface SystemNotificationsResponse {
  notifications: SystemNotification[];
}

const POLLING_INTERVAL = 60000; // 60 segundos

const HIDDEN_NOTIFICATIONS_KEY = 'hiddenSystemNotifications';

function getHiddenNotifications(): string[] {
  try {
    const stored = localStorage.getItem(HIDDEN_NOTIFICATIONS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function setHiddenNotifications(ids: string[]): void {
  try {
    localStorage.setItem(HIDDEN_NOTIFICATIONS_KEY, JSON.stringify(ids));
  } catch {
    // Ignore localStorage errors
  }
}

function isNotificationActive(notification: SystemNotification): boolean {
  const now = new Date();

  if (notification.from) {
    const fromDate = new Date(notification.from);
    if (now < fromDate) {
      return false;
    }
  }

  if (notification.until) {
    const untilDate = new Date(notification.until);
    if (now > untilDate) {
      return false;
    }
  }

  return true;
}

export function useSystemNotifications() {
  const { get } = useClient();
  const { user } = useAuthContext();
  const [hiddenNotificationIds, setHiddenNotificationIds] = useState<string[]>(getHiddenNotifications());

  const url = user?.organizationId ? `/system-notifications?organizationId=${user.organizationId}` : '/system-notifications';

  const { data } = useSWR<SystemNotificationsResponse>(
    url,
    async (url: string) => {
      const response = await get<SystemNotificationsResponse>(url);
      if (!response) throw new Error('Failed to fetch system notifications');
      return response;
    },
    {
      refreshInterval: POLLING_INTERVAL,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    },
  );

  const activeNotifications = data?.notifications.filter(
    (notification) => isNotificationActive(notification) && !hiddenNotificationIds.includes(notification.id),
  ) ?? [];

  const hideNotification = (id: string) => {
    const newHidden = [...hiddenNotificationIds, id];
    setHiddenNotificationIds(newHidden);
    setHiddenNotifications(newHidden);
  };

  return {
    notifications: activeNotifications,
    hideNotification,
  };
}
